import kyWithSentry from './helper/kyWithSentry';
import merge from 'lodash.merge';

import getShortFormVideoBreakdownURL from 'url-constructors/ShortFormVideo/getShortFormVideoBreakdownURL';

import { IBreakdownURLArgs } from 'url-constructors/ShortFormVideo/getShortFormVideoBreakdownURL';
import {
  YOUTUBE_SHORTS_ID,
  WESEE_ID,
} from 'components/TopicFilters/constants/platformFilter.contants';

const getSpecificRequest =
  (props: IBreakdownURLArgs): any =>
  (): any =>
    kyWithSentry.get(getShortFormVideoBreakdownURL(props)).json();

const addContentType = (trends: any, contentType: string): any => {
  if (!trends || trends.length === 0) return;
  return trends.map((trend: any) => {
    const data = trend.data.map((trend: any) => ({ ...trend, contentType }));
    return { ...trend, data };
  });
};

/**
 * Initially shortFormVideo data had only one type of data (pgc) and that was why we did not need to set the type in url to send a request to server.
 * Now the data has two types - pgc and ugc. So we need to set them in the server requests.
 */

const shortFormVideoBreakdownInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  const pgcViewsProps = merge(
    {},
    interceptionData,
    { metricType: 'views' },
    { contentType: 'pgc' }
  );
  const pgcCreationsProps = merge(
    {},
    interceptionData,
    { metricType: 'creations' },
    { contentType: 'pgc' }
  );
  const ugcViewsProps = {
    ...interceptionData,
    metricType: 'views',
    contentType: 'ugc',
  };
  const ugcCreationsProps = {
    ...interceptionData,
    metricType: 'creations',
    contentType: 'ugc',
  };

  const withoutPGC =
    interceptionData.platform === YOUTUBE_SHORTS_ID ||
    interceptionData.platform === WESEE_ID;

  const pgcViewsUrl = getSpecificRequest(pgcViewsProps);
  const pgcCreationsUrl = getSpecificRequest(pgcCreationsProps);
  const ugcViewsUrl = getSpecificRequest(ugcViewsProps);
  const ugcCreationsUrl = getSpecificRequest(ugcCreationsProps);

  const shortFormVideoBreakdownPGCViewsFetch = async (): Promise<any> =>
    !withoutPGC && pgcViewsUrl();

  const shortFormVideoBreakdownPGCCreationsFetch = async (): Promise<any> =>
    !withoutPGC && pgcCreationsUrl();

  const shortFormVideoBreakdownUGCViewsFetch = async (): Promise<any> =>
    ugcViewsUrl();

  const shortFormVideoBreakdownUGCCreationsFetch = async (): Promise<any> =>
    ugcCreationsUrl();

  try {
    if (withoutPGC) {
      // No PGC data
      const promises: Promise<any>[] = [
        shortFormVideoBreakdownUGCViewsFetch(),
        shortFormVideoBreakdownUGCCreationsFetch(),
      ];

      const [shortFormVideoUGCViewsData, shortFormVideoUGCCreationsData] =
        await Promise.all(promises);

      return new Response(
        JSON.stringify({
          shortFormVideoPGCViewsData: addContentType([], 'pgc-views'),
          shortFormVideoPGCCreationsData: addContentType([], 'pgc-creations'),
          shortFormVideoUGCViewsData: addContentType(
            shortFormVideoUGCViewsData,
            'ugc-views'
          ),
          shortFormVideoUGCCreationsData: addContentType(
            shortFormVideoUGCCreationsData,
            'ugc-creations'
          ),
          ugcViewsUrl: getShortFormVideoBreakdownURL(ugcViewsProps),
          ugcCreationsUrl: getShortFormVideoBreakdownURL(ugcCreationsProps),
        })
      );
    } else {
      const result = await Promise.allSettled([
        shortFormVideoBreakdownPGCViewsFetch(),
        shortFormVideoBreakdownPGCCreationsFetch(),
        shortFormVideoBreakdownUGCViewsFetch(),
        shortFormVideoBreakdownUGCCreationsFetch(),
      ]);

      const [
        shortFormVideoPGCViewsData,
        shortFormVideoPGCCreationsData,
        shortFormVideoUGCViewsData,
        shortFormVideoUGCCreationsData,
      ] = result;

      return new Response(
        JSON.stringify({
          ...(shortFormVideoPGCViewsData.status === 'fulfilled' && {
            shortFormVideoPGCViewsData: addContentType(
              shortFormVideoPGCViewsData.value,
              'pgc-views'
            ),
          }),
          ...(shortFormVideoPGCCreationsData.status === 'fulfilled' && {
            shortFormVideoPGCCreationsData: addContentType(
              shortFormVideoPGCCreationsData.value,
              'pgc-creations'
            ),
          }),
          ...(shortFormVideoUGCViewsData.status === 'fulfilled' && {
            shortFormVideoUGCViewsData: addContentType(
              shortFormVideoUGCViewsData.value,
              'ugc-views'
            ),
          }),
          ...(shortFormVideoUGCCreationsData.status === 'fulfilled' && {
            shortFormVideoUGCCreationsData: addContentType(
              shortFormVideoUGCCreationsData.value,
              'ugc-creations'
            ),
          }),
          pgcViewsUrl: getShortFormVideoBreakdownURL(pgcViewsProps),
          pgcCreationsUrl: getShortFormVideoBreakdownURL(pgcCreationsProps),
          ugcViewsUrl: getShortFormVideoBreakdownURL(ugcViewsProps),
          ugcCreationsUrl: getShortFormVideoBreakdownURL(ugcCreationsProps),
        })
      );
    }
  } catch (error) {
    const errorInfo = await error.response.json();
    return new Response(
      JSON.stringify({
        reason: {
          error: errorInfo.message,
          errorInfo,
        },
      })
    );
  }
};

export default shortFormVideoBreakdownInterceptor;
